import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppStore } from '../../stores/appStore';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { toast } from '../../components/ui/toast';

const editOrgSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less'),
  domain: z.string()
    .optional()
    .refine(
      (val) => !val || val.includes('.'),
      'Domain must be valid (e.g., example.com)'
    ),
});

export function EditOrganization() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { currentOrg, loading, error, fetchOrganization, updateOrg, clearError } = useAppStore();

  const form = useForm<z.infer<typeof editOrgSchema>>({
    resolver: zodResolver(editOrgSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      domain: '',
    },
  });

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    clearError();
    if (id) {
      fetchOrganization(id).then((org) => {
        form.reset({
          name: org.name,
          domain: org.domain || '',
        });
      }).catch((error) => {
        // Error is already handled in the store and displayed via error state
        console.error('Failed to fetch organization:', error);
      });
    }
  }, [id, fetchOrganization, clearError, form]);

  useEffect(() => {
    if (currentOrg) {
      form.reset({
        name: currentOrg.name,
        domain: currentOrg.domain || '',
      });
    }
  }, [currentOrg, form]);

  if (!isSuperAdmin()) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Only super administrators can edit organizations.
          </AlertDescription>
        </Alert>
        <Link to="/dashboard/orgs">
          <Button variant="outline">Back to Organizations</Button>
        </Link>
      </div>
    );
  }

  const onSubmit = async (data: z.infer<typeof editOrgSchema>) => {
    if (!id) return;
    try {
      await updateOrg(id, data);
      toast({
        title: 'Organization Updated',
        description: 'Organization details have been successfully updated.',
      });
      navigate('/dashboard/orgs');
    } catch (error: any) {
      // Error already set in store and shown via Alert, also show toast
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update organization',
        variant: 'destructive'
      });
    }
  };

  if (loading && !currentOrg) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/orgs">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Organizations
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Organization</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Update organization details</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
          <CardDescription>
            Update the information for this organization
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Update Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corporation" {...field} />
                    </FormControl>
                    <FormDescription>
                      The display name for this organization
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="domain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="acme.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Associated domain for this organization
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {loading ? 'Updating...' : 'Update Organization'}
                </Button>
                <Link to="/dashboard/orgs">
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
